import { StatusBadge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { leadStatus } from '@/theme/status';
import type { Kanban, LeadCard } from '@/types/crm';
import { LeadCardItem } from './LeadCardItem';
import { columnHasMore, formatNumber, summarizeKanban } from './crm-format';

/**
 * El tablero del CRM: una columna por estado del lead.
 *
 * `GET .../crm/kanban` NO pagina: devuelve las 4 columnas con hasta `limitePorColumna` tarjetas
 * cada una (default 30, máx. 200) y el total REAL por columna. Por eso el orden importa y la
 * pantalla lo dice: se pintan `columnas` tal como llegan (el backend las manda en el orden del
 * embudo) y cuando una columna tiene más de las que entraron se avisa con el total, en vez de
 * dejar creer que eso es todo lo que hay.
 *
 * Es un componente de PROPS (sin hooks), así que el smoke lo puede pintar con un kanban inventado.
 */

export interface LeadsKanbanProps {
  kanban: Kanban;
  /** Abre la ficha del lead. */
  onOpenLead: (lead: LeadCard) => void;
  /** Reclasifica el lead a mano. */
  onStatusLead: (lead: LeadCard) => void;
}

export function LeadsKanban({ kanban, onOpenLead, onStatusLead }: LeadsKanbanProps) {
  if (kanban.columnas.length === 0) {
    return (
      <EmptyState
        title="Sin columnas que mostrar"
        description="El tablero se arma con los estados del embudo (Frío, Tibio, Caliente y Cliente). Vuelve a cargar la pantalla."
      />
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-caption text-ink-muted">{summarizeKanban(kanban)}</p>

      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
        {kanban.columnas.map((columna) => {
          const meta = leadStatus[columna.estadoLead];

          return (
            <section
              key={columna.estadoLead}
              aria-label={`Columna ${meta.label}`}
              className="flex min-w-0 flex-col rounded-card border border-line bg-surface-muted/40 p-3"
            >
              <header className="flex items-center justify-between gap-2">
                <StatusBadge label={meta.label} tone={meta.tone} />
                <span className="text-micro text-ink-muted tabular">
                  {formatNumber(columna.items.length)} de {formatNumber(columna.total)}
                </span>
              </header>

              {columna.items.length === 0 ? (
                <p className="mt-3 rounded-panel border border-dashed border-line-strong px-3 py-6 text-center text-caption text-ink-faint">
                  Nada en «{meta.label}»
                </p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {columna.items.map((lead) => (
                    <LeadCardItem
                      key={lead.conversationId}
                      lead={lead}
                      onOpen={onOpenLead}
                      onStatus={onStatusLead}
                    />
                  ))}
                </ul>
              )}

              {columnHasMore(columna, kanban.limitePorColumna) ? (
                <p className="mt-3 text-micro text-ink-faint">
                  {`Hay ${formatNumber(columna.total)} en este estado y se están viendo los ${
                    columna.items.length
                  } más recientes: sube «tarjetas por columna» para ver el resto.`}
                </p>
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}
